import type { ComponentProps } from 'react';
import { CheckinsThisWeek, FollowedAchievementsUnder } from '@/components/engagement/FollowedAchievements';
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartsRow } from '@/components/performance/ChartsRow';
import { edgeFromTotals } from '@/lib/directionalEdge';
import { useQueries, useQuery } from '@tanstack/react-query';
import { subscriptionApi } from '@/api/client';
import { livePositionsTotal, openTradePnl } from '@/lib/openPnl';
import { useLivePrices, markPriceOf } from '@/hooks/useLivePrices';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowDown, ArrowUp, ChartBar, Coins, Info, Percent, Play, Pulse, Scales, TrendDown, Trophy } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { usePeriodRecap, useVisit } from '@/hooks/useEngagement';
import { firstPeriodWithTrade, PERF_PERIODS, periodFigures, summarisePeriod, type PerfPeriod } from '@/lib/engagement.core';
import { lossContext } from '@/lib/lessons.core';
import { usePortfolioExposure, useEquityHistory, useHistoryExposure, type PortfolioExposure, exposureFromHistoryGroups } from '@/hooks/usePortfolio';
import { fmtFixed, fmtPct, fmtUsd, intlLocale, dateLocale, withSign } from '@/lib/format';
import { isLiveMoneySession, isPaperSession } from '@/lib/liveMoney';
import { AssetSplitSwitch, SymbolLabel } from '@/components/AssetType';
import { type AssetClass, instrumentOf, shownClass, useInstruments } from '@/lib/instruments';
import { useSyncedPreference } from '@/hooks/useSyncedPreference';
import { focusLink } from '@/lib/focusLink.core';

/****************
 * Section one: the user's own book, split so nothing is quietly mixed.
 *
 * Guillaume, 2026-09-03, on the first version: "we need to divide this section
 * in a few category and sub-category. First Live (open trade) vs Historical
 * (Closed trades). Second Paper vs Live... We should clearly present what
 * results coming from paper trading with the ones coming from live trading."
 *
 * Two divisions, and they are different in kind:
 *
 *   PAPER vs LIVE is a filter over everything below it. Demo results and real
 *   money must never be summed into one headline, so the scope is a control at
 *   the top and the whole section obeys it. There is no combined option at all,
 *   and the section opens on whichever scope is actually RUNNING — a "+$4,000"
 *   that turns out to be a simulation is the single worst number this product
 *   could show.
 *
 *   OPEN vs HISTORICAL is neither a filter nor a split: every square carries
 *   both. "How am I doing" is realised AND open, and putting them in separate
 *   blocks made a reader look in two places for one answer.
 *
 * BROKER is the third division and is built but nearly invisible: only Phemex
 * exists, so the chips render only when a second broker appears. The plumbing
 * is here now so that adding one is a data change rather than a UI project.
 ****************/

const usd = (v: number, digits = 0) =>
  fmtUsd(v === 0 ? 0 : v, digits);


/** Slice colours for the asset ring. Deliberately not the long/short palette:
 *  green and red mean direction everywhere else in this product. */

/*
 * Two scopes, never a combined one.
 *
 * Guillaume: "There should not be 3 options Live, Paper, All there should just
 * be Live and Paper we should not combine them." He is right that the third was
 * a trap — a total that silently blends a simulation into real money is the one
 * number this dashboard must never produce, and offering it at all invites
 * someone to read it.
 */
type Scope = 'live' | 'paper';

interface PortfolioSectionProps {
  sessions: any[];
  positions: any[] | undefined;
  positionsLoading: boolean;
  sessionsLoading: boolean;
  sessionsError: boolean;
  /** The LIVE dot, shown beside the title (review #78). */
  live?: React.ReactNode;
  /** Next step + lesson, at the bottom of the card (review #78). */
  footer?: React.ReactNode;
}

export function PortfolioSection({
  sessions,
  positions,
  positionsLoading,
  sessionsLoading,
  sessionsError,
  live,
  footer,
}: PortfolioSectionProps) {
  const { t } = useTranslation('dashboard');
  /*
   * Three kinds of session, two tabs, and the third belongs in neither.
   *
   * A signal session places nothing and holds nothing. It was landing in Paper
   * because `is_paper` is true for anything without a credential, which put a
   * $1,000 declared wallet into paper equity and drew orders that reached no
   * book into all three charts. It is excluded here as well as in the backend:
   * the tab COUNTS come from this list, so filtering only server-side would
   * have left "Paper (3)" over two sessions. 2026-09-05.
   *
   * session_type is the mapper's own verdict and covers what the raw flag does
   * not: SIM_ traders, a deleted credential, a testnet key.
   */
  // Live = real money on a key we still hold; see lib/liveMoney (2026-09-28).
  const liveSessions = useMemo(() => sessions.filter(isLiveMoneySession), [sessions]);
  const paperSessions = useMemo(() => sessions.filter(isPaperSession), [sessions]);

  /*
   * Opens on whichever scope is actually RUNNING, not merely on whichever
   * exists. A user with three stopped live sessions and one paper session
   * running should land on paper, because that is where something is happening.
   * Live wins a tie: real money outranks a simulation when both are live.
   *
   * `scope` stays null until the user chooses, so the automatic choice keeps
   * following the sessions as they start and stop rather than freezing on the
   * first render.
   */
  const isOpen = (x: any) => x.status === 'active' || x.status === 'running';
  const running = (list: any[]) => list.some(isOpen);
  // The counts on Live / Paper are the sessions running now (Guillaume 10-29), not every one ever made.
  const liveOpen = liveSessions.filter(isOpen).length;
  const paperOpen = paperSessions.filter(isOpen).length;
  const [scope, setScope] = useState<Scope | null>(null);
  const effectiveScope: Scope =
    scope ??
    (running(liveSessions) ? 'live'
      : running(paperSessions) ? 'paper'
      : liveSessions.length > 0 ? 'live'
      : paperSessions.length > 0 ? 'paper'
      : 'live');

  const scoped = effectiveScope === 'live' ? liveSessions : paperSessions;

  // Brokers present in the current scope. Paper and Telegram sessions carry an
  // empty broker, which means "none" rather than "unknown", so they are not
  // offered as a filter.
  const brokers = useMemo(
    () => [...new Set(scoped.map((s) => s.broker).filter(Boolean))] as string[],
    [scoped],
  );
  const [broker, setBroker] = useState<string | null>(null);
  const activeBroker = broker && brokers.includes(broker) ? broker : null;

  const shown = useMemo(
    () => (activeBroker ? scoped.filter((s) => s.broker === activeBroker) : scoped),
    [scoped, activeBroker],
  );
  const shownIds = useMemo(() => new Set(shown.map((s) => String(s.id))), [shown]);

  // Positions belong to a session, so scoping them is the same filter applied
  // through subscription_id rather than a second definition of "live".
  const scopedPositions = useMemo(
    () => (positions || []).filter((p) => shownIds.has(String(p.subscription_id))),
    [positions, shownIds],
  );
  const { data: scopeEquity, isLoading: scopeEquityLoading } = useEquityHistory('24h', effectiveScope);

  /****************
   * ONE BROKER CHOSEN: THAT BROKER'S KEYS' MONEY. 2026-10-14.
   *
   * Guillaume, with KuCoin chosen: "These figures are not accurate when you
   * select broker." Equity, its 24h change, open PnL and the open count were
   * the whole live scope's -- Phemex included -- because the equity read only
   * knew live/paper. The server keeps a series per key ("key:<id>"), so the
   * chosen broker's keys are read one by one and added up. Each key is a
   * separate account, so nothing is counted twice.
   ****************/
  const brokerCreds = useMemo(
    () => (activeBroker
      ? [...new Set(shown.map((s: any) => Number(s.credential_id)).filter((id) => id > 0))]
      : []),
    [activeBroker, shown],
  );
  const keyEquity = useQueries({
    queries: brokerCreds.map((id) => ({
      queryKey: ['portfolio', 'equity', '24h', `key:${id}`],
      queryFn: () => subscriptionApi.getEquityHistory('24h', `key:${id}` as `key:${number}`),
      refetchInterval: 30_000,
      staleTime: 15_000,
    })),
  });
  const keyData = keyEquity.map((q) => q.data);
  const keyReady = keyData.length > 0 && keyData.every(Boolean);
  const brokerEquity = useMemo(() => {
    if (!keyReady) return undefined;
    const rows = keyData as any[];
    if (rows.length === 1) return rows[0];
    let current = 0, changeAbs = 0, oldest = 0, open = 0;
    const book: any[] = [];
    const asOf: string[] = [];
    for (const e of rows) {
      current += e.current ?? 0;
      changeAbs += e.change_abs ?? 0;
      // What the window started on: now, less trading, less capital moved.
      oldest += (e.current ?? 0) - (e.capital_added ?? 0) - (e.change_abs ?? 0);
      open += e.open_positions ?? 0;
      book.push(...(e.open_book ?? []));
      if (e.live_as_of) asOf.push(e.live_as_of);
    }
    return {
      ...rows[0],
      points: rows.flatMap((e) => e.points ?? []),
      current,
      change_available: rows.every((e) => e.change_available),
      change_abs: changeAbs,
      change_pct: oldest !== 0 ? (changeAbs / oldest) * 100 : 0,
      open_positions: open,
      open_book: book,
      live_as_of: asOf.sort()[0],
      // Measured per key; not added up into one sentence.
      outside_trading: undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyReady, ...keyData]);
  const equity: any = activeBroker && brokerCreds.length > 0 ? brokerEquity : scopeEquity;
  const equityLoading = activeBroker && brokerCreds.length > 0 ? !keyReady : scopeEquityLoading;

  /****************
   * WHEN WE HOLD NO ROWS, THE ACCOUNT'S OWN BOOK IS THE TRUTH.
   *
   * Guillaume, 2026-09-06: "in Trades we say he got 3 Open but next to it we
   * say these 3 positions got 0 market exposure and 0$ unrealised PnL and the
   * open button is unclickable."
   *
   * Four statements about the same three positions and only the count was
   * right. The count had already been switched to the broker's figure; every
   * other number here still came from replicated_position rows, and on a
   * credential eighteen sessions share we hold none of those — the broker
   * reports one merged position per symbol and it cannot be attributed to a
   * session, so nothing is stored.
   *
   * The venue's positions need no attribution to be true. They are the
   * ACCOUNT's, and that is how they are labelled.
   *
   * Substituted, never added. Ours are used whenever we have any — the paper
   * and signal case, where there is no broker to ask and our rows are the only
   * record, and the single-session case where attribution actually worked.
   * Summing the two would count one position twice, which is the mistake that
   * produced a $722 wallet for a $671 account.
   */
  const accountBook = (equity as any)?.open_book as any[] | undefined;
  const effectivePositions =
    scopedPositions.length > 0 ? scopedPositions : (accountBook ?? []);
  // True when the figures below describe the account rather than this scope's
  // own sessions, so the UI can say so instead of implying an attribution it
  // does not have.
  const bookIsAccountLevel = scopedPositions.length === 0 && (accountBook?.length ?? 0) > 0;

  /*
   * The asset-type choice beside Live/Paper (Guillaume, 2026-10-04): every
   * tile and chart follows it -- "Net PnL, Trades, Win Rate, Open Exposure,
   * Long vs Short and Leverage too". Open figures come from the positions of
   * that type; closed figures from the history endpoint's per-symbol rows,
   * counted as the session totals are, so the types sum to All.
   */
  const [savedAssetClass, setAssetClass] = useSyncedPreference<AssetClass | 'all'>('copyfi.dashboard.portfolioAssetClass', 'all');
  const assetClass = shownClass(savedAssetClass);
  const { data: instrumentReg } = useInstruments();
  const classPositions = useMemo(
    () => assetClass === 'all' ? effectivePositions
      : effectivePositions.filter((p: any) => instrumentOf(p.symbol ?? p.asset, instrumentReg).class === assetClass),
    [effectivePositions, assetClass, instrumentReg],
  );
  const exposure = usePortfolioExposure(classPositions);
  const inClass = useCallback(
    (sym: string) => assetClass === 'all' || instrumentOf(sym, instrumentReg).class === assetClass,
    [assetClass, instrumentReg],
  );

  /*
   * The three charts draw OPEN positions when there are any, and closed trades
   * when there are not.
   *
   * Guillaume: "Long vs Short, Asset Split, Leverage are all Empty, they should
   * show my historical values and there should be a note about the Open value."
   * An empty square is a wasted one, and a user with nothing open still has a
   * history worth the same three questions. Which population is being drawn is
   * stated on every card rather than inferred from whether it looks busy.
   */
  /*
   * ...and which one is drawn is now the reader's choice, not the data's.
   *
   * Guillaume, 2026-09-05: "there is no way for me to compare the current
   * repartition of the open positions vs the closed historical ones." The rule
   * below picked one and hid the other, so the comparison could not be made at
   * all -- and with anything open, the history was simply unreachable.
   *
   * The automatic rule stays as the DEFAULT (`null` = follow the data), so a
   * user with nothing open still lands on something worth looking at. Choosing
   * pins it. Open is offered only when there is something open, because an
   * empty ring the user selected on purpose reads as a fault.
   */
  /*
   * How many positions are open, taking the broker's word over ours.
   *
   * `exposure.openCount` counts the rows WE hold. On a credential with several
   * sessions on it we hold none — the broker reports one merged position per
   * symbol and it cannot be attributed to a session, so nothing is stored — and
   * the dashboard then reports 0 for an account with four live positions.
   * Guillaume, 2026-09-06, with Phemex open beside it.
   *
   * The snapshot's figure comes from the exchange and needs no attribution to
   * be true: it says how many the ACCOUNT holds. Ours is used when it is the
   * larger of the two, which is the paper and signal case where there is no
   * broker to ask and our rows are the only record.
   */
  // The broker's count has no symbols, so with a type chosen ours is the count.
  const openNow = assetClass === 'all' ? Math.max(exposure.openCount, equity?.open_positions ?? 0) : exposure.openCount;

  // With a broker chosen, only its keys' closed trades (Guillaume 11-03: Hyperliquid still showed every broker's).
  const history = useHistoryExposure(effectiveScope, activeBroker ? brokerCreds : undefined);
  /*
   * The tab is offered when anything is open, from either source.
   *
   * It was `exposure.openCount > 0` over our own rows alone, so on a shared
   * credential the page reported three open positions and disabled the control
   * that shows them. An empty ring a user selected on purpose reads as a fault;
   * a disabled tab beside a non-zero count reads as a broken page, which it was.
   */
  const hasOpen = openNow > 0 && classPositions.length > 0;
  const [basis, setBasis] = useState<'open' | 'closed' | null>(null);
  const chartBasis: 'open' | 'closed' =
    basis === 'open' && !hasOpen ? 'closed' : (basis ?? (hasOpen ? 'open' : 'closed'));
  const classHistory = useMemo(
    () => assetClass === 'all' || !history.groups ? history.exposure : exposureFromHistoryGroups(history.groups, inClass),
    [assetClass, history.groups, history.exposure, inClass],
  );
  const charted = chartBasis === 'open' ? exposure : classHistory;

  // One type's closed results, for the shown sessions (the broker tab applies).
  const classResults = useMemo(() => {
    if (assetClass === 'all' || !history.results) return null;
    let closed = 0, wins = 0, losses = 0, pnl = 0;
    for (const r of history.results) {
      if (!shownIds.has(String(r.subscription_id)) || !inClass(r.symbol)) continue;
      closed += r.closed; wins += r.wins; losses += r.losses; pnl += r.realized_pnl;
    }
    return { closed, wins, losses, pnl };
  }, [assetClass, history.results, shownIds, inClass]);

  const realizedPnl = useMemo(
    () => classResults ? classResults.pnl : shown.reduce((s: number, x: any) => s + (x.user_total_pnl_usd || 0), 0),
    [shown, classResults],
  );
  /*
   * With a type chosen, realised PnL is summed from the trades of that type.
   * A session recorded only as a total (before trades were kept one by one)
   * belongs to no type, so the types would not add up to All with nothing
   * saying why. The amount is shown instead (2026-10-05).
   */
  const outsideTypes = useMemo(() => {
    if (!classResults || !history.results) return 0;
    let traded = 0;
    for (const r of history.results) if (shownIds.has(String(r.subscription_id))) traded += r.realized_pnl;
    const sessions = shown.reduce((s: number, x: any) => s + (x.user_total_pnl_usd || 0), 0);
    return sessions - traded;
  }, [classResults, history.results, shownIds, shown]);
  /*
   * Open PnL from whichever source the positions came from.
   *
   * Read from our session rows it was $0 on an account $42 down, beside a card
   * saying three positions were open. The venue states this per position and
   * the reconciler stores it; when we are showing the account's book, its own
   * numbers are what add up.
   */
  /****************
   * THE VENUE'S NUMBER, WHENEVER THERE IS ONE.
   *
   * Guillaume, 2026-09-07: "In my dashboard it say Open PnL -40.18$ but on his
   * dashboard he see -41.11$."
   *
   * Two live sources for one quantity, and which you saw depended on whether
   * your session rows happened to be populated. Measured on his account the
   * same minute:
   *
   *   session rows   -45.2750   (sub 256 -45.2685, sub 249 -0.0065)
   *   account book   -44.6628   (ENA -44.6556, DOGE -0.0272, HBAR +0.0200)
   *
   * $0.61 apart, nearly all of it ENAUSDT, and stable across reads -- so not a
   * timing artefact. Two numbers, both live, both shown as fact.
   *
   * The account book is the exchange's own figure for the account. Our
   * per-session number is an attribution we compute. When they disagree the
   * attribution is what is wrong, so the TOTAL now comes from the venue
   * whenever the venue has answered, and falls back to our rows only where
   * there is no broker to ask -- paper and signal sessions.
   *
   * Per-session unrealised is untouched: a session page must still say what
   * IT is holding. This is only about the account-level total, which had no
   * business being computed two ways.
   ****************/
  /*
   * THE SAME POSITIONS AS THE OPEN-TRADES TRAY, PRICED THE SAME WAY.
   * Guillaume, 2026-09-22: the Dashboard's open PnL is "supposed to be the sum
   * of all Open Trades, just like the amount at the top right of the Open
   * Trades" tray -- and it was a third figure (the reconciler's 15 s book).
   * Same query key as the tray, so one fetch feeds both; live prices on top.
   */
  const { data: openBook } = useQuery({
    queryKey: ['open-positions', 'tray'],
    queryFn: () => subscriptionApi.getOpenPositions(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });
  /*
   * The live figure is priced INSIDE <LiveNetPnl> below, not here. Priced here
   * (2026-09-22), the whole section -- charts and all -- re-rendered on every
   * price tick, which arrives every 2 s; Guillaume: the Dashboard renders very
   * slowly. Only the two numbers that move now re-render.
   */
  const trayRows = effectiveScope !== 'paper' ? openBook?.positions : undefined;
  // With a broker chosen, only its keys' positions (2026-10-14).
  const liveRows = useMemo(
    () => (trayRows ?? []).filter((p: any) => inClass(p.symbol)
      && (!activeBroker || brokerCreds.includes(Number(p.credential_id)))),
    [trayRows, inClass, activeBroker, brokerCreds],
  );
  const unrealizedFallback = useMemo(
    () => assetClass !== 'all'
      ? classPositions.reduce((s: number, x: any) => s + (Number(x.unrealized_pnl) || 0), 0)
      : (accountBook?.length ?? 0) > 0
        ? (accountBook ?? []).reduce((s: number, x: any) => s + (Number(x.unrealized_pnl) || 0), 0)
        : shown.reduce((s: number, x: any) => s + (x.user_unrealized_pnl_usd || 0), 0),
    [assetClass, classPositions, accountBook, shown],
  );
  const closedTrades = useMemo(
    () => classResults ? classResults.closed : shown.reduce((s: number, x: any) => s + (x.trade_count || 0), 0),
    [shown, classResults],
  );
  const wins = useMemo(() => classResults ? classResults.wins : shown.reduce((s: number, x: any) => s + (x.win_count || 0), 0), [shown, classResults]);
  const losses = useMemo(() => classResults ? classResults.losses : shown.reduce((s: number, x: any) => s + (x.loss_count || 0), 0), [shown, classResults]);
  const winRate = wins + losses > 0 ? (wins / (wins + losses)) * 100 : null;
  const activeSessions = shown.filter((s: any) => s.status === 'active' || s.status === 'running').length;
  /* The period (10-25). Opens on "since your last visit" when there was one. */
  const visit = useVisit();
  const [periodChoice, setPeriod] = useState<PerfPeriod | null>(null);
  // Opens on the first period with a closed trade in this scope (round 9 #304), read off the
  // whole-record recap; until it is here, the old default (Last visit when there was one).
  const allRecap = usePeriodRecap('all');
  const period: PerfPeriod = periodChoice ?? (allRecap.data
    ? firstPeriodWithTrade(allRecap.data.trades, visit.data?.since, (tr) => shownIds.has(String(tr.subscription_id)) && inClass(tr.symbol))
    : (visit.data?.since ? 'visit' : 'all'));
  const periodRecap = usePeriodRecap(period);
  const periodSum = useMemo(
    () => summarisePeriod(periodRecap.data?.trades, (tr) => shownIds.has(String(tr.subscription_id)) && inClass(tr.symbol)),
    [periodRecap.data, shownIds, inClass],
  );
  /* "no trade closed" only when the period's own count agrees (round 9: on All the tiles
     read the trade recap and the line under Net PnL the session totals; if the recap came back
     short, the tiles said "no trade closed" beside "29 closed"). */
  const noneClosedText = period === 'all' && closedTrades > 0 ? '—' : t('portfolio.fact.noneClosed');
  const visitLoss = period === 'visit' ? lossContext(periodRecap.data) : null;

  /* An asset type chosen and its history not here yet: a skeleton, never the
     all-types totals under that type's name (Mishka, 2026-10-21). */
  const classPending = assetClass !== 'all' && Boolean(history.isLoading);

  return (
    <div className="mb-8 glass-card rounded-xl p-5">
      {/* ONE CARD (Guillaume 10-25): "Since you were away" and "Your performance"
          together, a period selector, and Live/Paper, broker and asset type in
          one control instead of three spread out. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-base font-semibold text-foreground flex items-center gap-2">{t('portfolio.title')}{live}</h2>
        <div className="flex flex-wrap items-center gap-1 rounded-full border border-border/60 bg-white/[0.02] p-1" data-testid="perf-filters">
          {/* Round 11 #503: the period with the other controls, first. */}
          <Tabs value={period} onValueChange={(v) => setPeriod(v as PerfPeriod)}>
            <TabsList className="h-7 bg-transparent" data-testid="perf-period">
              {PERF_PERIODS.map((p) => (
                <TabsTrigger key={p} value={p} className="text-[11px] px-2.5 h-5 rounded-full">{t(`portfolio.period.${p}`)}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <span className="h-4 w-px bg-border/60" aria-hidden />
          {liveSessions.length > 0 && paperSessions.length > 0 && (<>
          <Tabs value={effectiveScope} onValueChange={(v) => setScope(v as Scope)}>
            <TabsList className="h-7 bg-transparent">
              <TabsTrigger value="live" className="text-[11px] px-2.5 h-5 rounded-full">
                {t('portfolio.live')}{` (${liveOpen})`}
              </TabsTrigger>
              <TabsTrigger value="paper" className="text-[11px] px-2.5 h-5 rounded-full">
                {t('portfolio.paper')}{` (${paperOpen})`}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="h-4 w-px bg-border/60" aria-hidden />
          </>)}
          {brokers.length > 1 && (
            <>
              <Tabs value={activeBroker ?? 'all'} onValueChange={(v) => setBroker(v === 'all' ? null : v)}>
                <TabsList className="h-7 bg-transparent">
                  <TabsTrigger value="all" className="text-[11px] px-2.5 h-5 rounded-full">{t('portfolio.allBrokers')}</TabsTrigger>
                  {brokers.map((b) => (
                    <TabsTrigger key={b} value={b} className="text-[11px] px-2.5 h-5 rounded-full capitalize">{b}</TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <span className="h-4 w-px bg-border/60" aria-hidden />
            </>
          )}
          <AssetSplitSwitch value={assetClass} onChange={setAssetClass} />
        </div>
      </div>



      {/* Packed (round 7 #100, Guillaume 10-26: "the most info in the smallest room"): Equity and
          the period's result stacked on the left, the facts two to a row on the right. */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] mb-5">
        {/* Round 8 #201: the period's result at the LEFT of Equity, side by side. */}
        <div className="grid gap-4 grid-cols-2 content-start">
        {/* The result of the period. "All" is realised + open, from the
            session totals; a period is what closed in it (the recap's trades,
            cut by the same filters). */}
        {period === 'all' ? (
          <Figure label={t('portfolio.netPnl')} info={t('portfolio.netPnl.info')} loading={sessionsLoading || classPending}>
            <div className="[&>p:first-child]:text-2xl [&>p:first-child]:mt-1">
              <LiveNetPnl realizedPnl={realizedPnl} liveRows={liveRows} fallback={unrealizedFallback} failed={sessionsError} usd={usd} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {winRate === null ? t('portfolio.nothingSettled')
                : t('portfolio.periodLine', { n: closedTrades.toLocaleString(intlLocale()), won: wins.toLocaleString(intlLocale()), lost: losses.toLocaleString(intlLocale()), rate: fmtPct(winRate, 0) })}
            </p>
            {Math.abs(outsideTypes) >= 0.01 && (
              <p className="text-[11px] text-muted-foreground mt-1">
                {t('portfolio.outsideTypes', { amount: withSign(outsideTypes >= 0, usd(outsideTypes, 2)) })}
              </p>
            )}
            {/* Trading on this key that CopyFi did not place (F-19): outside Net PnL on purpose. */}
            {equity?.outside_trading?.measured && (equity.outside_trading.pnl ?? 0) !== 0 ? (
              <p className="text-[11px] text-muted-foreground mt-1">
                {t('portfolio.outside', {
                  amount: `${withSign((equity.outside_trading.pnl ?? 0) >= 0, usd(equity.outside_trading.pnl ?? 0, 2))}`,
                })}
                {equity.outside_trading.symbol_names?.length ? (
                  <span className="opacity-70">
                    {' '}{t('portfolio.outsideOn', { symbols: equity.outside_trading.symbol_names.map((s) => instrumentOf(s, instrumentReg).display).join(', ') })}
                    {(equity.outside_trading.symbols ?? 0) > equity.outside_trading.symbol_names.length
                      ? ` ${t('portfolio.outsideMore', { n: (equity.outside_trading.symbols ?? 0) - equity.outside_trading.symbol_names.length })}`
                      : ''}
                  </span>
                ) : null}
              </p>
            ) : null}
          </Figure>
        ) : (
          <Figure label={t(`portfolio.result.${period}`)} info={t('portfolio.result.info')} loading={periodRecap.isLoading || sessionsLoading}>
            {periodRecap.isError ? <p className="text-2xl font-semibold mt-1">—</p> : (
              <>
                <p className={`text-2xl font-semibold tabular-nums mt-1 ${periodSum.net > 0 ? 'text-success' : periodSum.net < 0 ? 'text-danger' : ''}`}>
                  {withSign(periodSum.net >= 0, usd(periodSum.net, 2))}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {periodSum.closed === 0 ? t('portfolio.periodQuiet')
                    : t('portfolio.periodLine', { n: periodSum.closed, won: periodSum.wins, lost: periodSum.losses, rate: fmtPct((100 * periodSum.wins) / Math.max(1, periodSum.wins + periodSum.losses), 0) })}
                </p>
              </>
            )}
          </Figure>
        )}

        {/* Equity: where the account is now, whatever the period. */}
        <Figure label={t('portfolio.equity')} info={t('portfolio.equity.info')} loading={equityLoading}>
          {/* "Not measured yet" is not "$0" (a session ten minutes old has no
              snapshot until the hourly job runs). */}
          <p className="text-2xl font-semibold tabular-nums mt-1">
            {equity && (equity.points?.length ?? 0) > 0 ? usd(equity.current, 2) : '—'}
          </p>
          {equity?.change_available ? (
            <p className={`text-xs mt-1 ${equity.change_abs >= 0 ? 'text-success' : 'text-danger'}`}>
              {withSign(equity.change_abs >= 0, usd(equity.change_abs, 2))}
              <span className="text-muted-foreground"> ({withSign(equity.change_pct >= 0, fmtPct(equity.change_pct === 0 ? 0 : equity.change_pct, 2))} · 24h)</span>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">{t('portfolio.measuringSinceToday')}</p>
          )}
          {equity?.live_as_of ? (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {t('portfolio.asOf', { time: new Date(equity.live_as_of).toLocaleTimeString(dateLocale()) })}
            </p>
          ) : null}
        </Figure>
        <FollowedAchievementsUnder />{/* round 11 #504: below Result + Equity (engagement) */}
        </div>

        {/* The facts beside it: the best trade (the trophy), the hardest one,
            what is open, and the exposure. */}
        <div className="grid gap-2 sm:grid-cols-2 content-start text-sm">
          {/* Every tile keeps its place when nothing closed (round 8 #202): "no trade closed" in it. */}
          {/* Round 12 (Guillaume): win rate, avg trade and profit factor each in their own box,
              plus avg win and avg loss: ten boxes, five even rows. */}
          {(() => {
            const f = periodFigures(periodSum);
            const none = periodSum.closed === 0;
            const muted = <span className="text-muted-foreground">{noneClosedText}</span>;
            const dash = <span className="text-muted-foreground">—</span>;
            const avgWin = periodSum.wins > 0 ? periodSum.grossWin / periodSum.wins : null;
            const avgLoss = periodSum.losses > 0 ? periodSum.grossLoss / periodSum.losses : null;
            const signed = (v: number) => <span className={v > 0 ? 'text-success' : v < 0 ? 'text-danger' : ''}>{withSign(v > 0, usd(v, 2))}</span>;
            return (
              <>
                <FactRow icon={<Percent size={14} className="text-muted-foreground" />} label={t('portfolio.winRate')} info={t('portfolio.fact.winRateInfo')}
                  value={none ? muted : f.winRate === null ? dash : <span>{fmtPct(f.winRate, 0)}</span>} />
                <FactRow icon={<Coins size={14} className="text-muted-foreground" />} label={t('portfolio.fact.avgTrade')} info={t('portfolio.fact.avgTradeInfo')}
                  value={none ? muted : f.avgTrade === null ? dash : signed(f.avgTrade)} />
                <FactRow icon={<ChartBar size={14} className="text-muted-foreground" />} label={t('portfolio.fact.profitFactor')} info={t('portfolio.fact.profitFactorInfo')}
                  value={none ? muted : f.profitFactor === null ? dash : <span>{f.profitFactor.toFixed(2)}</span>} />
                <FactRow icon={<ArrowUp size={14} className="text-success" />} label={t('portfolio.fact.avgWin')} info={t('portfolio.fact.avgWinInfo')}
                  value={none ? muted : avgWin === null ? dash : signed(avgWin)} />
                <FactRow icon={<ArrowDown size={14} className="text-danger" />} label={t('portfolio.fact.avgLoss')} info={t('portfolio.fact.avgLossInfo')}
                  value={none ? muted : avgLoss === null ? dash : signed(-avgLoss)} />
              </>
            );
          })()}
          <FactRow icon={<Trophy size={14} weight="fill" className="text-warning" />} label={t('portfolio.fact.best')}
            value={!periodSum.best ? <span className="text-muted-foreground">{noneClosedText}</span>
              : <span className="text-success"><SymbolLabel symbol={periodSum.best.symbol} showName={false} /> {withSign(true, usd(periodSum.best.realized_pnl, 2))}</span>} />
          {!periodSum.worst ? (
            <FactRow icon={<TrendDown size={14} className="text-danger" />} label={t('portfolio.fact.hardest')}
              value={<span className="text-muted-foreground">{noneClosedText}</span>} />
          ) : (
            <FactRow icon={<TrendDown size={14} className="text-danger" />} label={t('portfolio.fact.hardest')}
              value={<Link to={focusLink({ kind: 'trade', sessionId: periodSum.worst.subscription_id, tradeId: periodSum.worst.id, why: true }, 'web')} className="text-danger hover:underline"><SymbolLabel symbol={periodSum.worst.symbol} showName={false} /> {usd(periodSum.worst.realized_pnl, 2)}</Link>} />
          )}
          <FactRow icon={<Pulse size={14} className="text-primary" />} label={t('portfolio.fact.open')}
            value={<span>{t('portfolio.openCount', { n: openNow })}{bookIsAccountLevel ? <span className="ms-1 text-muted-foreground">{t('portfolio.atExchange')}</span> : null}</span>} />
          <FactRow icon={<Scales size={14} className="text-muted-foreground" />} label={t('portfolio.exposure')}
            info={bookIsAccountLevel ? t('portfolio.exposure.infoAccount') : t('portfolio.exposure.info')}
            value={positionsLoading ? <Skeleton className="h-4 w-20" /> : (
              <span>{usd(exposure.totalNotional, 2)}
                <span className="text-muted-foreground"> · {exposure.weightedLeverage === null ? t('portfolio.noLeverage') : t('portfolio.weighted', { lev: fmtFixed(exposure.weightedLeverage, 1) })}</span>
              </span>
            )} />
          <FactRow icon={<Play size={14} className="text-muted-foreground" />} label={t('portfolio.fact.running')}
            value={<span>{activeSessions}</span>} />
          <CheckinsThisWeek />{/* round 11 #505: under the facts (engagement) */}
          {period === 'visit' && visitLoss && visitLoss.verdict !== 'within' && (
            <p className="sm:col-span-2 text-xs text-warning border border-warning/40 rounded-lg px-3 py-2">{t('today.lossBeyond', { worst: visitLoss.traderWorstPct.toFixed(0) })}</p>
          )}
          {bookIsAccountLevel && <p className="sm:col-span-2 text-[10px] text-muted-foreground/80">{t('portfolio.fromExchange')}</p>}
        </div>
      </div>

      {/* One control for all three: they answer the same question about the
          same book, and three separate toggles would let them disagree. */}
      {/* Round 8 #200: the toggle at the LEFT of the sentence that describes it. */}
      <div className="flex items-center gap-3 mb-2">
        <Tabs value={chartBasis} onValueChange={(v) => setBasis(v as 'open' | 'closed')}>
          <TabsList className="h-7">
            <TabsTrigger value="open" className="text-[10px] px-2 h-5" disabled={!hasOpen}>
              {t('portfolio.tabOpen')}{hasOpen ? ` (${exposure.openCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="closed" className="text-[10px] px-2 h-5">{t('portfolio.tabClosed')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-xs text-muted-foreground">
          {chartBasis === 'open'
            ? t('portfolio.splitOpen')
            : t('portfolio.splitClosed')}
        </p>
      </div>

      {/* The shared row (components/performance/ChartsRow): identical order,
          info and alignment to the trader page, the profile and the session.
          Guillaume, 2026-09-25. */}
      <DashboardChartsRow
        openPositions={chartBasis === 'open' ? classPositions : null}
        assetClass={assetClass}
        onAssetClassChange={setAssetClass}
        mix={{
          longNotional: charted.longNotional,
          shortNotional: charted.shortNotional,
          longCount: charted.longCount,
          shortCount: charted.shortCount,
          byAsset: charted.byAsset.map((a) => ({ symbol: a.symbol, notional: a.notional, count: a.count })),
          weightedLeverage: charted.weightedLeverage,
          tradeCount: charted.openCount,
        }}
        basis={chartBasis}
        hideBasis
        /* The closed-trade result by side, on BOTH views. It sat behind the
           Closed tab, and a paper account with anything open opens on Open,
           so paper never showed it (Guillaume, 2026-09-25). */
        /* Closed view: realised, after fees. Open view: the open positions'
           PnL right now, priced live (Guillaume, 2026-09-25: "why can't you
           just share the PnL of the open trade"). */
        edge={classHistory.openCount > 0
          ? edgeFromTotals(classHistory.longPnl ?? 0, classHistory.shortPnl ?? 0,
              classHistory.longCount, classHistory.shortCount)
          : null}
        footer={<BasisNote basis={chartBasis} openCount={exposure.openCount} />}
      />
      {footer}
    </div>
  );
}

/** One figure of the card: its label (with the "i" that explains it), then its value. */
function Figure({ label, info, loading, children }: { label: string; info: string; loading?: boolean; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground cursor-help"><Info size={12} /></span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px] text-xs">{info}</TooltipContent>
        </Tooltip>
      </div>
      {loading ? <Skeleton className="h-8 w-32 mt-1.5" /> : children}
    </div>
  );
}

/** A short fact beside the figures: icon, label, value on one line. */
function FactRow({ icon, label, value, info }: { icon: React.ReactNode; label: string; value: React.ReactNode; info?: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/50 px-3 py-2 min-w-0" title={info}>
      {icon}
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="ms-auto text-xs font-medium tabular-nums truncate">{value}</span>
    </div>
  );
}

/****************
 * Shown ONLY when there is something the label does not already say.
 *
 * Guillaume, 2026-09-22: "there is 'Nothing open right now — this is your
 * closed history' but this is not necessary since it's already specified that
 * these data are for the closed trades."
 *
 * He is right: basisLabel above already prints "Closed trades" on the box, so
 * that sentence restated the heading two lines under it. The OTHER branch is
 * not redundant and stays -- "you have 3 open positions and they are not in
 * this chart yet" is a fact the label cannot carry, and its absence is what
 * makes an empty-looking chart read as broken.
 ****************/
type ChartBasis = 'open' | 'closed';

function BasisNote({ basis, openCount }: { basis: ChartBasis; openCount: number }) {
  const { t } = useTranslation('dashboard');
  if (basis === 'open' || openCount === 0) return null;
  return (
    <p className="text-[10px] text-muted-foreground/80 text-center mt-2">
      {t('portfolio.basisNote', { count: openCount })}
    </p>
  );
}

function Legend({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className={`w-2 h-2 rounded-full shrink-0 ${color}`} />
      <span className="flex-1">{label}</span>
      <span className="text-muted-foreground font-mono">{value}</span>
    </div>
  );
}

function EmptyChart() {
  const { t } = useTranslation('dashboard');
  return (
    <div className="flex flex-col items-center justify-center h-24 text-center">
      <span className="text-xs text-muted-foreground">{t('portfolio.empty')}</span>
      <span className="text-[10px] text-muted-foreground/70 mt-1">
        {t('portfolio.empty.hint')}
      </span>
    </div>
  );
}

/*
 * The Net PnL tile's two lines, the only part of this section that moves with
 * every price tick. Its own component so the tick re-renders these two lines
 * and nothing else.
 */
function LiveNetPnl({ realizedPnl, liveRows, fallback, failed, usd }: {
  realizedPnl: number;
  liveRows: { symbol: string; side: string; entry_price?: number; size?: number; unrealized_pnl?: number }[];
  fallback: number;
  failed: boolean;
  usd: (v: number, d?: number) => string;
}) {
  const { t } = useTranslation('dashboard');
  const livePrices = useLivePrices();
  const unrealizedPnl = liveRows.length > 0
    ? livePositionsTotal(liveRows, (sym) => markPriceOf(livePrices?.[sym]))
    : fallback;
  const total = realizedPnl + unrealizedPnl;
  return (
    <>
      <p className={`text-lg font-semibold mt-0.5 ${total >= 0 ? 'text-success' : 'text-danger'}`}>
        {failed ? '—' : `${withSign(total >= 0, usd(total, 2))}`}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">
        {t('portfolio.pnlClosed', { amount: usd(realizedPnl, 2) })}
        <span className="mx-1">·</span>
        <span className={unrealizedPnl >= 0 ? 'text-success/80' : 'text-danger/80'}>
          {t('portfolio.pnlOpen', { amount: `${withSign(unrealizedPnl >= 0, usd(unrealizedPnl, 2))}` })}
        </span>
      </p>
    </>
  );
}

/****************
 * THE CHART ROW, WITH THE OPEN VIEW PRICED LIVE.
 *
 * On the Open view the sentence is about the open positions, by their PnL
 * right now. Prices tick every 2 s, so the price hook lives HERE: only this
 * row re-renders on a tick, never the whole section (the 09-22 slowness).
 * The Closed view mounts plain ChartsRow and never subscribes to prices.
 ****************/
function DashboardChartsRow({ openPositions, ...rest }: { openPositions: any[] | null } & ComponentProps<typeof ChartsRow>) {
  if (!openPositions || openPositions.length === 0) return <ChartsRow {...rest} />;
  return <OpenPricedChartsRow openPositions={openPositions} {...rest} />;
}

function OpenPricedChartsRow({ openPositions, ...rest }: { openPositions: any[] } & ComponentProps<typeof ChartsRow>) {
  const livePrices = useLivePrices();
  let longPnl = 0, shortPnl = 0, longs = 0, shorts = 0;
  for (const p of openPositions) {
    const pnl = openTradePnl(p, (sym) => markPriceOf(livePrices?.[sym]), Number(p.unrealized_pnl) || 0);
    const dir = String(p.direction ?? p.side ?? '').toUpperCase();
    if (dir.startsWith('LONG') || dir === 'BUY') { longPnl += pnl; longs++; }
    else if (dir.startsWith('SHORT') || dir === 'SELL') { shortPnl += pnl; shorts++; }
  }
  return <ChartsRow {...rest} edge={edgeFromTotals(longPnl, shortPnl, longs, shorts, 'open')} edgeNote={null} />;
}
